import apiRequest from '@/utils/apiRequest';

/**
 * PG 公开配置
 */
export interface PgPublicConfig {
  enabled: boolean;
  sdkUrl: string;
  groupId?: number;
  showTournamentLink?: boolean;
  showBetHistoryLink?: boolean;
}

/** SDK 加载状态 */
let sdkLoaded = false;
let sdkLoading: Promise<void> | null = null;

type PgSdkWindow = Window & {
  PGSDK?: {
    launchGame?: (launcherUrl: string) => void;
  };
};

/**
 * 获取 PG 公开配置
 */
export async function getPgConfig(): Promise<PgPublicConfig> {
  return apiRequest<PgPublicConfig>('/public/pg/config', {
    method: 'GET',
  });
}

/**
 * 动态加载 PG SDK 脚本（仅浏览器环境）
 */
export function loadPgSdk(sdkUrl: string): Promise<void> {
  if (typeof document === 'undefined') return Promise.reject(new Error('loadPgSdk is browser-only'));
  if (sdkLoaded) return Promise.resolve();
  if (sdkLoading) return sdkLoading;

  sdkLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.onload = () => {
      sdkLoaded = true;
      resolve();
    };
    script.onerror = () => {
      sdkLoading = null;
      reject(new Error('PG SDK 加载失败'));
    };
    document.head.appendChild(script);
  });

  return sdkLoading;
}

/**
 * 启动 PG 游戏
 * 必须在用户点击事件的同步上下文中调用（仅浏览器环境）
 */
export function launchPgGame(launcherUrl: string): void {
  if (typeof window === 'undefined') return;
  const PGSDK = (window as PgSdkWindow).PGSDK;
  if (PGSDK && typeof PGSDK.launchGame === 'function') {
    PGSDK.launchGame(launcherUrl);
  } else {
    // SDK 不可用时降级为 window.open
    window.open(launcherUrl, '_blank');
  }
}

/**
 * 获取游戏启动 HTML
 */
export async function getLaunchHTML(groupId: number | undefined, clientOrigin: string): Promise<string> {
  const params = buildLaunchParams({ groupId, clientOrigin });
  return apiRequest<string>(`/pg/GetLaunchURLHTML${params}`, {
    method: 'POST',
    responseType: 'text',
  });
}

/**
 * 获取 Web 大厅 HTML
 */
export async function getWebLobbyHTML(groupId: number | undefined, clientOrigin: string): Promise<string> {
  const params = buildLaunchParams({ groupId, clientOrigin });
  return apiRequest<string>(`/pg/GetWebLobbyHTML${params}`, {
    method: 'POST',
    responseType: 'text',
  });
}

/**
 * 获取锦标赛 HTML
 */
export async function getTournamentHTML(groupId: number | undefined, clientOrigin: string): Promise<string> {
  const params = buildLaunchParams({ groupId, clientOrigin });
  return apiRequest<string>(`/pg/GetTournamentHTML${params}`, {
    method: 'POST',
    responseType: 'text',
  });
}

/**
 * PG 游戏项
 */
export interface PgGameItem {
  gameId: number;
  name: string;
  iconUrl: string;
  coverUrl: string;
  category: string;
}

/**
 * 获取 PG 游戏列表
 */
export async function getPgGameList(): Promise<PgGameItem[]> {
  return apiRequest<PgGameItem[]>('/public/pg/games', {
    method: 'GET',
  });
}

/**
 * 获取指定游戏的启动 HTML
 */
export async function getLaunchHTMLByGameId(gameId: number, groupId: number | undefined, clientOrigin: string): Promise<string> {
  const params = buildLaunchParams({ groupId, gameId, clientOrigin });
  return apiRequest<string>(`/pg/GetLaunchURLHTML${params}`, {
    method: 'POST',
    responseType: 'text',
  });
}

function buildLaunchParams(params: { groupId?: number; gameId?: number; clientOrigin: string }): string {
  const query = new URLSearchParams();
  if (params.groupId) {
    query.set('group_id', String(params.groupId));
  }
  if (params.gameId) {
    query.set('game_id', String(params.gameId));
  }
  query.set('clientOrigin', params.clientOrigin);
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

// PG 母注单投注记录接口
export interface PgBetSummaryRecord {
  id: string;
  betId: string;
  parentBetId: string;
  gameId: number;
  gameName?: string;
  iconUrl?: string;
  betAmount: number;
  winAmount: number;
  transferAmount: number;
  endRound: boolean;
  createDate: string;
  endTime?: number;
  playerName: string;
}

// 分页结果接口
interface PageResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number; // current page
  size: number;
}

/**
 * 获取用户 PG 投注记录列表
 */
export async function getPgTransactions(params?: {
  page?: number;
  pageSize?: number;
  parentBetId?: string;
}): Promise<PageResult<PgBetSummaryRecord>> {
  return apiRequest<PageResult<PgBetSummaryRecord>>('/pg/transactions', {
    method: 'POST',
    data: {
      page: params?.page ?? 0,
      pageSize: params?.pageSize ?? 20,
      parentBetId: params?.parentBetId,
    },
  });
}

/**
 * 获取 PG 投注详情 HTML
 */
export async function getBetDetailHTML(params: {
  betId: string;
  parentBetId: string;
  gameId: number;
}): Promise<string> {
  return apiRequest<string>('/pg/GetBetDetailHTML', {
    method: 'POST',
    data: params,
    responseType: 'text',
  });
}

/**
 * PG用户流水统计
 */
export interface PgUserStats {
  totalTurnover: string;
  totalProfit: string;
  totalLoss: string;
}

/**
 * 获取当前用户PG流水统计
 */
export async function getPgUserStats(): Promise<PgUserStats> {
  try {
    return await apiRequest<PgUserStats>('/user/pg/stats', {
      method: 'POST',
    });
  } catch {
    return { totalTurnover: '0', totalProfit: '0', totalLoss: '0' };
  }
}
